import React from 'react';
import {
  Building2,
  Users,
  Layers,
  Crown,
  RefreshCw,
  Plus,
  BarChart3,
} from 'lucide-react';
import { InnerLayout, PillNavItem } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { Button } from '../components/ui/Button';
import { useAdminPlans } from './admin/plans/hooks/useAdminPlans';
import { PlanCard } from './admin/plans/components/PlanCard';
import { PlanFormModal } from './admin/plans/components/PlanFormModal';

export const AdminPlansView: React.FC = () => {
  const {
    plans,
    isLoading,
    isRefreshing,
    handleRefresh,
    isModalOpen,
    setIsModalOpen,
    editingPlanId,
    name,
    setName,
    slug,
    setSlug,
    description,
    setDescription,
    maxMonthlySubmissions,
    setMaxMonthlySubmissions,
    maxTables,
    setMaxTables,
    maxTeamSeats,
    setMaxTeamSeats,
    retentionDays,
    setRetentionDays,
    maxStorageMB,
    setMaxStorageMB,
    maxFileSizeMB,
    setMaxFileSizeMB,
    priceMonthlyDollars,
    setPriceMonthlyDollars,
    priceYearlyDollars,
    setPriceYearlyDollars,
    isActive,
    setIsActive,
    isDefault,
    setIsDefault,
    featuresText,
    setFeaturesText,
    isSaving,
    handleOpenCreate,
    handleOpenEdit,
    handleSavePlan,
    handleDeletePlan,
  } = useAdminPlans();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <HedgehogSpinner size="lg" label="LOADING SUBSCRIPTION TIERS..." />
      </div>
    );
  }

  const adminNavItems: PillNavItem[] = [
    {
      id: 'overview',
      label: 'Platform Overview',
      icon: BarChart3,
      active: false,
      href: '/admin',
    },
    {
      id: 'organizations',
      label: 'Organizations',
      icon: Building2,
      active: false,
      href: '/admin/organizations',
    },
    {
      id: 'tenants',
      label: 'Users & Tenants',
      icon: Users,
      active: false,
      href: '/admin/tenants',
    },
    {
      id: 'queues',
      label: 'Queues & Workers',
      icon: Layers,
      active: false,
      href: '/admin/queues',
    },
    {
      id: 'plans',
      label: 'Plans & Pricing',
      icon: Crown,
      active: true,
      href: '/admin/plans',
    },
  ];

  return (
    <InnerLayout
      header={{
        title: 'Subscription Plans & Ingestion Tiers',
        description:
          'Configure dynamic quotas, monthly submission ceilings, datastore limits, and pricing.',
        actions: [
          {
            label: isRefreshing ? 'Refreshing...' : 'Refresh',
            icon: <RefreshCw size={14} className={isRefreshing ? 'animate-spin text-cf-orange' : ''} />,
            variant: 'secondary',
            disabled: isRefreshing,
            onClick: handleRefresh,
          },
          {
            label: 'Create Plan',
            icon: Plus,
            variant: 'primary',
            onClick: handleOpenCreate,
          },
        ],
      }}
      navItems={adminNavItems}
    >
      {/* Plans Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onEdit={() => handleOpenEdit(plan)}
            onDelete={() => handleDeletePlan(plan)}
          />
        ))}
      </div>

      {/* Create / Edit Plan Modal */}
      <PlanFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingPlanId={editingPlanId}
        name={name}
        setName={setName}
        slug={slug}
        setSlug={setSlug}
        description={description}
        setDescription={setDescription}
        maxMonthlySubmissions={maxMonthlySubmissions}
        setMaxMonthlySubmissions={setMaxMonthlySubmissions}
        maxTables={maxTables}
        setMaxTables={setMaxTables}
        maxTeamSeats={maxTeamSeats}
        setMaxTeamSeats={setMaxTeamSeats}
        retentionDays={retentionDays}
        setRetentionDays={setRetentionDays}
        maxStorageMB={maxStorageMB}
        setMaxStorageMB={setMaxStorageMB}
        maxFileSizeMB={maxFileSizeMB}
        setMaxFileSizeMB={setMaxFileSizeMB}
        priceMonthlyDollars={priceMonthlyDollars}
        setPriceMonthlyDollars={setPriceMonthlyDollars}
        priceYearlyDollars={priceYearlyDollars}
        setPriceYearlyDollars={setPriceYearlyDollars}
        isActive={isActive}
        setIsActive={setIsActive}
        isDefault={isDefault}
        setIsDefault={setIsDefault}
        featuresText={featuresText}
        setFeaturesText={setFeaturesText}
        isSaving={isSaving}
        onSubmit={handleSavePlan}
      />
    </InnerLayout>
  );
};
